import { useEffect, useMemo, useState } from 'react';
import { useReloadOnChange } from '../lib/changes';
import { ApiError, api, hoursLabel, slotLabel, type EditOutcome, type EditRecord, type Flag, type Period, type PeriodShift, type Site } from '../lib/api';
import { AddDialog, HandoverDialog, ReassignDialog, RemoveDialog, RetimeDialog, handoverPartner } from './ShiftEdit';
import { addDays, dayLabel, quarterOfWeek, weekdayShort } from '../lib/dates';
import { navigate } from '../lib/router';
import { quarterLabel } from '../lib/api';

/**
 * One site's period (a Tuesday week, or a McLeod cycle): every shift by day,
 * the validation findings as the period stands, and per-shift editing —
 * reassign (with "who could fill this?"), retime, add, remove — each with a
 * reason for the audit log. An edit that would introduce an error is shown
 * back with the reasons and can be applied with an override reason — except
 * a credentialing error (D41), which is refused with no override offered.
 */
/**
 * Take a finalized or published week back to a draft (D52).
 *
 * Three screens told the office to do this and no button existed, so the only route was
 * CallCovered calling the API. It is deliberately not a quiet one-click: reopening DELETES the
 * week's shifts, and on a published week it takes a schedule away from physicians who can
 * already see it and may have booked flights around it.
 */
function Reopen({ siteId, periodStart, status, editedBy, onDone }: { siteId: string; periodStart: string; status: string; editedBy: string | null; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const published = status === 'published';

  const go = async (discardEdits: boolean) => {
    setBusy(true);
    setProblem(null);
    try {
      await api.reopenWeek(siteId, periodStart, { reopenReason: reason.trim(), reopenedBy: editedBy, discardEdits });
      setOpen(false);
      setReason('');
      onDone();
    } catch (e) {
      // The week has per-shift changes that re-finalizing would undo. Say how many, and let
      // them say again that those are to go — the server will not do it on one press.
      const n = e instanceof ApiError && e.body && typeof e.body === 'object' && 'editCount' in e.body ? Number((e.body as { editCount: unknown }).editCount) : 0;
      if (n > 0 && !discardEdits) {
        const ok = window.confirm(
          `This week has ${n} change${n === 1 ? '' : 's'} made shift by shift — a swap or a sick day.\n\n` +
            `Reopening throws ${n === 1 ? 'it' : 'them'} away and puts the week back to its plan. ${n === 1 ? 'It stays' : 'They stay'} in the edit history.\n\n` +
            `Reopen anyway?`,
        );
        if (ok) return go(true);
        setProblem('Left as it is.');
      } else {
        setProblem(e instanceof ApiError ? e.message : (e as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <p className="reopen-offer muted small">
        Need to change who works this week rather than one shift?{' '}
        <button type="button" className="linklike" onClick={() => setOpen(true)}>Reopen it</button> — back to a draft, so it can be planned again.
      </p>
    );
  }
  return (
    <div className="card reopen">
      <h2>Reopen this {status} week?</h2>
      <p className="small">
        It goes back to being a draft and <strong>its shifts are deleted</strong>, so it can be planned again from scratch.
        {published && <> This week is <strong>published</strong>: physicians can see it now, and reopening takes it off their pages. Anyone who has booked travel around it needs telling.</>}
      </p>
      <label className="field">
        <span>Why are you reopening it? (recorded with the change)</span>
        <input value={reason} maxLength={500} autoFocus placeholder="e.g. Cho withdrew for the whole week" onChange={(e) => setReason(e.target.value)} />
      </label>
      {problem && <p className="error small">{problem}</p>}
      <div className="formactions">
        <button type="button" className="primary" disabled={busy || !reason.trim()} onClick={() => void go(false)}>
          {busy ? 'Reopening…' : 'Reopen it'}
        </button>
        <button type="button" disabled={busy} onClick={() => { setOpen(false); setProblem(null); }}>Leave it as it is</button>
      </div>
    </div>
  );
}

export function SiteWeek({ siteId, periodStart, sites, editedBy }: { siteId: string; periodStart: string; sites: Site[]; editedBy: string }) {
  const site = sites.find((s) => s.id === siteId);
  const [period, setPeriod] = useState<Period | null>(null);
  const [flags, setFlags] = useState<Flag[] | null>(null);
  const [history, setHistory] = useState<EditRecord[] | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog | null>(null);

  const reload = () => {
    // Cleared on every attempt: a load that succeeds has to be able to take the banner down.
    setError(null);
    api.period(siteId, periodStart).then(setPeriod).catch((e: Error) => setError(e.message));
    api.validation(siteId, periodStart).then((v) => setFlags(v.flags)).catch(() => setFlags(null));
    if (showHistory) api.edits(siteId, periodStart).then(setHistory).catch(() => setHistory(null));
  };
  useEffect(reload, [siteId, periodStart, showHistory]);
  useReloadOnChange(reload);

  const byDay = useMemo(() => {
    const m = new Map<string, PeriodShift[]>();
    for (const s of period?.assignments ?? []) m.set(s.date, [...(m.get(s.date) ?? []), s]);
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([date, rows]) => [date, rows.sort(bySlotThenStart)] as const);
  }, [period]);

  const done = (outcome: EditOutcome, what: string) => {
    setDialog(null);
    setFlags(outcome.flags);
    setToast(`${what}. ${outcome.introduced.length ? `${outcome.introduced.length} error(s) overridden.` : 'No new errors.'}`);
    api.period(siteId, periodStart).then(setPeriod);
    if (showHistory) api.edits(siteId, periodStart).then(setHistory);
  };

  if (error) return <p className="error">Couldn't load this period ({error}).</p>;
  if (!period || !site) return <p className="muted">Loading…</p>;

  const errors = (flags ?? []).filter((f) => f.severity === 'error');
  const warnings = (flags ?? []).filter((f) => f.severity === 'warning');
  const dates = period.assignments.length ? [...new Set(period.assignments.map((a) => a.date))].sort() : [periodStart];
  const last = site.cycleLengthDays ? addDays(periodStart, site.cycleLengthDays - 1) : addDays(periodStart, 6);
  // A period can be changed shift by shift as soon as it has been planned — a draft included
  // (D51). The by-week route is the normal one and sits above the days; these are the exception.
  const editable = period.status === 'draft' || period.status === 'finalized' || period.status === 'published';
  const draft = period.status === 'draft';
  const byWeek = period.source === 'engine' && draft;
  // Back to a draft (D52). Only a week the engine built: an imported one has no plan to
  // rebuild from, and a sign-up one holds physicians' own claims (D112). Both routes refuse
  // outright, so offering the control would only produce an error.
  const reopenable = period.source !== 'imported' && period.source !== 'sign-up' && (period.status === 'finalized' || period.status === 'published');

  return (
    <section className="weekpage">
      <div className="crumbs">
        <button className="linklike" type="button" onClick={() => navigate(`/admin/grid?quarter=${quarterOfWeek(periodStart)}`)}>‹ {quarterLabel(quarterOfWeek(periodStart))} grid</button>
      </div>
      <header className="weekhead">
        <h1>
          <span className={`letter site-${siteId}`}>{site.letter}</span> {site.name} · {site.cycleLengthDays ? 'cycle' : 'week'} of {dayLabel(periodStart)}
        </h1>
        <div className="chips">
          <span className={`pill status-${period.status}`}>{period.status}</span>
          <span className="pill">{period.source === 'imported' ? `imported from ${period.importedFrom ?? 'QGenda'}` : 'built by the engine'}</span>
          <span className="pill">{period.editCount} edit{period.editCount === 1 ? '' : 's'}</span>
          {period.overrideReason && <span className="pill warn" title={period.overrideReason}>finalized with override</span>}

        </div>
      </header>

      {flags === null ? <p className="muted">Checking the rules…</p> : (
        <details className="flags" open={errors.length > 0}>
          <summary>
            {errors.length ? <span className="err">{errors.length} error{errors.length === 1 ? '' : 's'}</span> : <span className="ok">No errors</span>}
            {' · '}{warnings.length} warning{warnings.length === 1 ? '' : 's'}
          </summary>
          <ul>
            {errors.map((f, i) => <li key={`e${i}`} className="err">{f.message}</li>)}
            {warnings.map((f, i) => <li key={`w${i}`} className="warn">{f.message}</li>)}
          </ul>
        </details>
      )}

      {toast && <p className="toast" onClick={() => setToast(null)}>{toast}</p>}

      {reopenable && <Reopen siteId={siteId} periodStart={periodStart} status={period.status} editedBy={editedBy} onDone={() => { api.period(siteId, periodStart).then(setPeriod); setToast('Reopened. It is a draft again — change it, then finalize and publish.'); }} />}

      {byWeek && (
        <div className="weekfirst">
          <button type="button" className="primary" onClick={() => navigate(`/admin/sites/${siteId}/periods/${periodStart}/build`)}>
            Change who works this week
          </button>
          <p className="muted small">
            That is the usual way: pick who holds each position for the whole {site.cycleLengthDays ? 'cycle' : 'week'}. Changing a single
            day below is the exception — for a swap or a sick day. Nothing here is visible to any physician until the week is finalized
            and published.
          </p>
        </div>
      )}
      {!editable && <p className="muted">This period is a {period.status} and cannot be changed here.</p>}

      <ol className="days">
        {byDay.map(([date, rows]) => (
          <li key={date} className="day">
            <div className="dayhead">
              <span className="weekday">{weekdayShort(date)}</span>
              <span className="date">{dayLabel(date).replace(/^\w+, /, '')}</span>
            </div>
            <table className="shifts-table">
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id} className={s.physician ? '' : 'open-row'}>
                    <td className="slot">{slotLabel(s.positionSlot)}</td>
                    <td className="hours">{s.start && s.end ? hoursLabel(s.start, s.end, s.crossesMidnight) : 'on call'}</td>
                    <td className="who">{s.physician ? `${s.physician.lastName}, ${s.physician.firstName}` : <em>open</em>}{s.origin === 'manual' && <span className="pill tiny" title="changed by hand after import/finalize">edited</span>}</td>
                    <td className="actions">
                      {editable && (
                        <>
                          <button type="button" onClick={() => setDialog({ kind: 'reassign', shift: s })}>Reassign</button>
                          <button type="button" onClick={() => setDialog({ kind: 'retime', shift: s })}>Retime</button>
                          {handoverPartner(s, period.assignments) && (
                            <button type="button" onClick={() => setDialog({ kind: 'handover', shift: s })} title="Cover changed hands at a different time — a delayed flight, say. Moves both shifts together.">Handover</button>
                          )}
                          <button type="button" className="danger" onClick={() => setDialog({ kind: 'remove', shift: s })}>Remove</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </li>
        ))}
      </ol>

      {editable && (
        <p>
          <button type="button" className="primary" onClick={() => setDialog({ kind: 'add' })}>Add a shift</button>
          {' '}
          <button type="button" className="linklike" onClick={() => setShowHistory((v) => !v)}>{showHistory ? 'hide' : 'show'} edit history</button>
        </p>
      )}

      {showHistory && (
        <section className="history">
          <h2>Edit history</h2>
          {history === null ? <p className="muted">Loading…</p> : history.length === 0 ? <p className="muted">No edits yet.</p> : (
            <ul>
              {history.map((h) => (
                <li key={h.id}>
                  <span className="pill tiny">{h.action}</span> {new Date(h.editedAt).toLocaleString()} · <strong>{h.editedBy}</strong> — {h.reason}
                  {h.overrideReason && <span className="warn"> · override: {h.overrideReason}</span>}
                  <div className="muted small">{describeChange(h)}</div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {dialog?.kind === 'reassign' && (
        <ReassignDialog site={site} shift={dialog.shift} editedBy={editedBy} periodStart={periodStart} onClose={() => setDialog(null)} onDone={(o) => done(o, 'Reassigned')} />
      )}
      {dialog?.kind === 'retime' && (
        <RetimeDialog site={site} shift={dialog.shift} editedBy={editedBy} periodStart={periodStart} onClose={() => setDialog(null)} onDone={(o) => done(o, 'Retimed')} />
      )}
      {dialog?.kind === 'remove' && (
        <RemoveDialog site={site} shift={dialog.shift} editedBy={editedBy} periodStart={periodStart} onClose={() => setDialog(null)} onDone={(o) => done(o, 'Removed')} />
      )}
      {dialog?.kind === 'handover' && handoverPartner(dialog.shift, period.assignments) && (
        <HandoverDialog site={site} shift={dialog.shift} outgoing={handoverPartner(dialog.shift, period.assignments)!} editedBy={editedBy} periodStart={periodStart} onClose={() => setDialog(null)} onDone={(o) => done(o, 'Handover moved')} />
      )}
      {dialog?.kind === 'add' && (
        <AddDialog site={site} dates={rangeDates(periodStart, last)} existingDates={dates} editedBy={editedBy} periodStart={periodStart} onClose={() => setDialog(null)} onDone={(o) => done(o, 'Added')} />
      )}
    </section>
  );
}

type Dialog = { kind: 'reassign'; shift: PeriodShift } | { kind: 'retime'; shift: PeriodShift } | { kind: 'remove'; shift: PeriodShift } | { kind: 'handover'; shift: PeriodShift } | { kind: 'add' };

/* ---------- helpers ---------- */

function bySlotThenStart(a: PeriodShift, b: PeriodShift): number {
  return a.positionSlot.localeCompare(b.positionSlot) || (a.start ?? '~').localeCompare(b.start ?? '~');
}

function rangeDates(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
  return out;
}

function describeChange(h: EditRecord): string {
  const who = (x: EditRecord['before']) => (x?.physicianId === undefined ? '' : x?.physicianId === null ? 'open' : String(x.physicianId));
  const when = (x: EditRecord['before']) => (x?.start ? `${x.start}-${x.end}` : x ? 'on call' : '');
  if (h.action === 'reassign') return `${h.before?.date} ${h.before?.positionSlot}: ${who(h.before)} → ${who(h.after)}`;
  if (h.action === 'retime') return `${h.before?.date} ${h.before?.positionSlot}: ${when(h.before)} → ${when(h.after)}`;
  if (h.action === 'add') return `${h.after?.date} ${h.after?.positionSlot}: ${who(h.after)} ${when(h.after)}`;
  if (h.action === 'remove') return `${h.before?.date} ${h.before?.positionSlot}: ${who(h.before)} ${when(h.before)} removed`;
  if (h.action === 'reopen-discard') return 'reopened; earlier edits abandoned';
  return '';
}
